import { Controller } from "@hotwired/stimulus";
import {debounce, isNull} from "lodash";

export default class extends Controller {
    static targets = [
        'searchForm',
        'filtersForm',
        'loadMoreButton',
        'searchProductsInput',
        'offsetInput',
        'product',
        'catalogFiltersSidebar',
        'isFilterInput',
    ];

    initialize() {
        if (!this.hasSearchFormTarget) {
            return;
        }
        this.searchByTerm = debounce(this.searchByTerm, 500).bind(this);
        this.searchFormSubmitted = this.searchFormSubmitted.bind(this);

        if (this.hasSearchFormTarget) {
            this.searchFormTarget.addEventListener(
                'submit',
                this.searchFormSubmitted,
            );
            this.#keepFocusOnSearchInputAfterSubmit();
        }
    }

    disconnect() {
        if (this.hasSearchFormTarget) {
            this.searchFormTarget.removeEventListener(
                'submit',
                this.searchFormSubmitted,
            );
            this.#keepFocusOnSearchInputAfterSubmit();
        }
    }

    #keepFocusOnSearchInputAfterSubmit() {
        this.searchProductsInputTargets.forEach(el => {
            const value = el.value;
            if (value.length >= 1) {
                el.closest('.input-wrapper').classList.add('focus');
                el.focus();
                el.value = '';
                el.value = value;
            }
        });
    }

    searchFormSubmitted() {
        this.#addFetchEventListener();
        // this.searchProductsInputTarget.blur();
        document.body.classList.add('is-searching');
    }

    #addFetchEventListener() {
        document.addEventListener(
            'turbo:before-fetch-request',
            this.#appendTurboToHeaders,
            { once: true },
        );
    }

    #appendTurboToHeaders(event) {
        const wantsTurboStream = event.detail.fetchOptions.referrer !== event.detail.url;

        if (wantsTurboStream) {
            const { pathname } = new URL(event.detail.url);
            if (pathname.includes('/produkte')) {
                const { headers } = event.detail.fetchOptions || {};
                if (headers) {
                    headers.Accept = ['text/vnd.turbo-stream.html', headers.Accept].join(', ');
                }
            }
        }
    }

    searchByTerm(newProduct = false) {
        if (!newProduct) {
            this.searchFormTarget.querySelector('input[name="groupName"]').value = '';
            this.searchFormTarget.querySelector('input[name="category"]').value = '';
        }

        this.searchFormTarget.requestSubmit();
    }

    loadMore(event) {
        event.preventDefault();

        this.offsetInputTarget.value = this.productTargets.length;

        this.searchProducts();

        this.offsetInputTarget.value = 0;
    }

    searchProducts() {
        this.#addFetchEventListener();
        this.#preventSubmittingEmptyInputs(this.filtersFormTarget);
        this.filtersFormTarget.requestSubmit();
    }

    searchProductsFromCatalog() {
        this.#addFetchEventListener();
        this.#preventSubmittingEmptyInputs(this.searchFormTarget);
        this.searchFormTarget.requestSubmit();
    }

    evaluateLoadMoreButtonVisibility(event) {
        this.loadMoreButtonTarget.dataset.shownProducts = parseInt(this.loadMoreButtonTarget.dataset.shownProducts, 10) + parseInt(event.detail.shownProducts, 10);

        if (this.loadMoreButtonTarget.dataset.shownProducts === this.loadMoreButtonTarget.dataset.totalProducts) {
            this.loadMoreButtonTarget.classList.add('d-none');
        }
    }

    goToProduct(event) {
        event.preventDefault();

        if (window.Turbo) {
            window.Turbo.visit(event.currentTarget.href);
        } else {
            window.location = event.currentTarget.href;
        }
    }

    openCatalogFilters(event) {
        event.stopPropagation();
        this.catalogFiltersSidebarTarget.classList.add('open');
        // document.body.classList.add('has-sidebar');
    }

    closeCatalogFilters() {
        this.catalogFiltersSidebarTarget.classList.remove('open');
        // document.body.classList.remove('has-sidebar');
    }

    selectFilter() {
        this.isFilterInputTarget.value = 'true';
        this.searchProducts();
    }

    selectSearchByGroupName(event) {
        const { currentTarget } = event;
        currentTarget.classList.add('disabled');
        const { groupName } = currentTarget.dataset;

        let groupNameInput = this.filtersFormTarget.querySelector('input[name="groupName"]');

        if (!isNull(groupNameInput)) {
            groupNameInput.value = groupName;
        } else {
            groupNameInput = document.createElement('input');
            groupNameInput.type = 'hidden';
            groupNameInput.name = 'groupName';
            groupNameInput.value = groupName;
            this.filtersFormTarget.append(groupNameInput);
        }

        this.searchProducts();
    }

    goToProductDetailPage(event) {
        const { currentTarget } = event;
        currentTarget.classList.add('disabled');
        const { sku } = currentTarget.dataset;
        let skuInput = this.filtersFormTarget.querySelector('input[name="sku"]');

        if (!isNull(skuInput)) {
            skuInput.value = sku;
        } else {
            skuInput = document.createElement('input');
            skuInput.type = 'hidden';
            skuInput.name = 'sku';
            skuInput.value = sku;
            this.filtersFormTarget.append(skuInput);
        }

        this.#addCategoryToRequest(event, this.filtersFormTarget)
        this.searchProducts();
    }

    goToNewProductDetailPage(event) {
        const { currentTarget } = event;
        currentTarget.classList.add('disabled');
        const { sku, groupName } = currentTarget.dataset;
        let skuInput = this.searchFormTarget.querySelector('input[name="sku"]');
        let groupNameInput = this.searchFormTarget.querySelector('input[name="groupName"]');

        if (!isNull(skuInput)) {
            skuInput.value = sku;
        } else {
            skuInput = document.createElement('input');
            skuInput.type = 'hidden';
            skuInput.name = 'sku';
            skuInput.value = sku;
            this.searchFormTarget.append(skuInput);
        }

        if (!isNull(groupNameInput)) {
            groupNameInput.value = groupName;
        } else {
            groupNameInput = document.createElement('groupName');
            groupNameInput.type = 'hidden';
            groupNameInput.name = 'groupName';
            groupNameInput.value = groupName;
            this.searchFormTarget.append(groupNameInput);
        }

        this.#addCategoryToRequest(event, this.searchFormTarget)
        this.searchByTerm(true);
    }

    #addCategoryToRequest(event, form) {
        const { category } = event.currentTarget.dataset;
        if (category) {
            let categoryInput = form.querySelector('input[name="category"]');
            if (!isNull(categoryInput)) {
                categoryInput.value = category;
            } else {
                categoryInput = document.createElement('input');
                categoryInput.type = 'hidden';
                categoryInput.name = 'category';
                categoryInput.value = category;
                form.append(categoryInput);
            }
        }
    }

    searchByGroupNameFromCatalog(event) {
        const { currentTarget } = event;
        currentTarget.classList.add('disabled');
        const { groupName } = currentTarget.dataset;

        let groupNameInput = this.searchFormTarget.querySelector('input[name="groupName"]');

        if (!isNull(groupNameInput)) {
            groupNameInput.value = groupName;
        } else {
            groupNameInput = document.createElement('input');
            groupNameInput.type = 'hidden';
            groupNameInput.name = 'groupName';
            groupNameInput.value = groupName;
            this.searchFormTarget.append(groupNameInput);
        }

        this.#addCategoryToRequest(event, this.searchFormTarget);
        this.searchProductsFromCatalog();
    }

    sortBySpecification = (event) => {
        const { currentTarget } = event;
        currentTarget.classList.add('disabled');
        const { specificationCode, sortingDirection } = currentTarget.dataset;

        let specificationCodeInput = this.filtersFormTarget.querySelector('input[name="spec"]');
        let sortingDirectionInput = this.filtersFormTarget.querySelector('input[name="sortDirection"]');

        if (!isNull(specificationCodeInput)) {
            specificationCodeInput.value = specificationCode;
        } else {
            specificationCodeInput = document.createElement('input');
            specificationCodeInput.type = 'hidden';
            specificationCodeInput.name = 'spec';
            specificationCodeInput.value = specificationCode;
            this.filtersFormTarget.append(specificationCodeInput);
        }

        if (!isNull(sortingDirectionInput)) {
            sortingDirectionInput.value = sortingDirection;
        } else {
            sortingDirectionInput = document.createElement('input');
            sortingDirectionInput.type = 'hidden';
            sortingDirectionInput.name = 'sortDirection';
            sortingDirectionInput.value = sortingDirection;
            this.filtersFormTarget.append(sortingDirectionInput);
        }

        this.searchProducts();
    };

    #preventSubmittingEmptyInputs(target) {
        const inputs = Array.from(target.getElementsByTagName('input'));
        inputs.forEach((input) => {
            if (!input.value) {
                input.name = '';
            }
        });
    }
}
